import { IApi } from '../../api'
import { createRequestTypes } from '../../helpers/helpers'
import { fetchAttachmentLink } from '../channels/attachmentLinkRequest'
import { createAction } from '../types'

export const prefix = 'box'

export const SET_BOX_VIEWER_LINK = `${prefix}/SET_BOX_VIEWER_LINK`

export const BOX_VIEWER_MODE = {
  DRAWER: 'drawer',
  MODAL: 'modal',
} as const

export type BoxViewerMode = (typeof BOX_VIEWER_MODE)[keyof typeof BOX_VIEWER_MODE]

export const setBoxViewerLink = (
  link: string,
  mode: BoxViewerMode = BOX_VIEWER_MODE.DRAWER,
  boxFileId?: string,
) => ({
  type: SET_BOX_VIEWER_LINK,
  data: { link, mode, boxFileId },
})

export const SET_BOX_VIEWER_MODE = `${prefix}/SET_BOX_VIEWER_MODE`

/** Moves an open preview between drawer and modal without refetching or reloading it. */
export const setBoxViewerMode = (mode: BoxViewerMode) => ({
  type: SET_BOX_VIEWER_MODE,
  data: { mode },
})

export const GET_EMBED_LINK = createRequestTypes(prefix, `GET_EMBED_LINK`)

export const [GET_EMBED_LINK_REQUEST, GET_EMBED_LINK_SUCCESS, GET_EMBED_LINK_FAILURE] =
  GET_EMBED_LINK

/**
 * Correlation id for an in-flight embed-link fetch.
 *
 * Embed links are slow enough that a user browsing documents can easily have
 * two in the air at once, and nothing about the response says which request it
 * answers — so the reducer used to apply whichever landed last. That surfaced
 * two ways: retargeting the preview quickly could leave the wrong document on
 * screen, and a response arriving after the user closed the panel would set
 * `boxViewLink` again and reopen it.
 *
 * Stamping the id here rather than at the call sites means every caller —
 * including the ones that never touch this file — is correlated for free.
 */
let embedRequestCounter = 0
const nextEmbedRequestId = () => {
  embedRequestCounter += 1
  return embedRequestCounter
}

export interface IEmbedLinkParams {
  requestId: number
}

export const getEmbedLink = createAction((fileId: string) => ({
  type: GET_EMBED_LINK,
  promise: (api: IApi) => api.box.getEmbedLink(fileId),
  params: { requestId: nextEmbedRequestId() },
}))

/**
 * Fetch an embed URL for a chat-attached file. Goes through a dedicated
 * server endpoint that uses Arc's CCG service-account, since chat
 * attachments live in Arc-owned folders the portal user can't see. Reuses
 * the GET_EMBED_LINK action types so the existing boxViewLink reducer
 * handles loading/success/failure transitions identically.
 */
export const getAttachmentEmbedLink = createAction((boxFileId: string) => ({
  type: GET_EMBED_LINK,
  promise: (api: IApi) => api.atlasAI.getAttachmentEmbedLink(boxFileId),
  params: { requestId: nextEmbedRequestId() },
}))

/**
 * Embed URL for a channel-message attachment.
 *
 * Keyed on the **attachment id**, not the Box file id: the server resolves the
 * owning channel from the attachment row and runs `requireChannelRead`, so a
 * guessable id can't be used to read a file out of a private channel. That is
 * why this can't reuse `getAttachmentEmbedLink` above, which authorizes on
 * nothing but "is an internal user".
 *
 * Reuses GET_EMBED_LINK like the Arc variant so the boxViewLink reducer handles
 * loading/success/failure identically — the URL that comes back is Box's
 * expiring embed link, which only renders inside BoxViewer's iframe. Navigating
 * to it as a top-level document yields a blank page, which is what the previous
 * `window.open` implementation did.
 *
 * Retried by `fetchAttachmentLink` like the timeline's own fetches: a legacy
 * Box-backed attachment reaches the viewer through this action and nowhere else, so
 * without it one blip drops the viewer straight to its failure state.
 */
export const getChannelAttachmentEmbedLink = createAction((attachmentId: string) => ({
  type: GET_EMBED_LINK,
  promise: (api: IApi) => fetchAttachmentLink(api, attachmentId),
  params: { requestId: nextEmbedRequestId() },
}))

export const HIDE_BOX_PREVIEW = `${prefix}/HIDE_BOX_PREVIEW`

export const hideBoxPreview = () => ({
  type: HIDE_BOX_PREVIEW,
})
